import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  TextField,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  FormHelperText,
  Checkbox,
  Switch,
} from "@mui/material";
import { useOutletContext, useNavigate } from "react-router-dom";
import * as yup from "yup";
import moment from "moment-timezone";
import { Form, Formik } from "formik";
import { Timestamp } from "firebase/firestore";
import { EditorState, ContentState } from "draft-js";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import htmlToDraft from "html-to-draftjs";
import { BottomButtons } from "../../EventBasicInfo/BottomButtons";
import ConfirmationDialog from "../../../ConfirmationDialog";
import { FormPrompt } from "../../../FormPrompt/FormPrompt";
import { DateTimeForm } from "../../EventBasicInfo/DateTimeForm";
import { rezoneMomentDateTime } from "../../../../utils";
import {
  createSession,
  updateSession,
} from "../../../../redux/features/adminEventsSlice";
import { LocationForm } from "../../EventBasicInfo/LocationForm";
import { DescriptionForm } from "../../EventBasicInfo/DescriptionForm";
import { PerformersForm } from "../../EventBasicInfo/PerformersForm";
import SessionMedia from "../SessionMedia/SessionMedia";
import {
  fetchPerformers,
  performersSliceSelector,
} from "../../../../redux/features/performersSlice";

const editorStateFromHtml = (html) => {
  if (!html) return EditorState.createEmpty();
  const contentBlock = htmlToDraft(html);
  if (contentBlock) {
    const contentState = ContentState.createFromBlockArray(
      contentBlock.contentBlocks,
    );
    const editorState = EditorState.createWithContent(contentState);
    return editorState;
  }
  return EditorState.createEmpty();
};

const validationSchema = yup.object({
  name: yup.string().required("Session Name is required"),
  description: yup.string().nullable(),
  enabled: yup.boolean(),
  publicFacing: yup.boolean(),
  placeName: yup
    .string()
    .trim()
    .when(["addressSelection"], (addressSelection, schema) => {
      return addressSelection[0] === "useEvent"
        ? schema.nullable(true)
        : schema.required("Location Name is required");
    }),
  addressLine1: yup
    .string()
    .trim()
    .when(["addressSelection"], (addressSelection, schema) => {
      return addressSelection[0] === "useEvent"
        ? schema.nullable(true)
        : schema.required("Address Line 1 is required");
    }),
  addressLine2: yup.string().trim(),
  city: yup
    .string()
    .trim()
    .when(["addressSelection"], (addressSelection, schema) => {
      return addressSelection[0] === "useEvent"
        ? schema.nullable(true)
        : schema.required("City is required");
    }),
  state: yup
    .string()
    .trim()
    .when(["addressSelection"], (addressSelection, schema) => {
      return addressSelection[0] === "useEvent"
        ? schema.nullable(true)
        : schema.required("State is required");
    }),
  zipcode: yup
    .string()
    .trim()
    .when(["addressSelection"], (addressSelection, schema) => {
      return addressSelection[0] === "useEvent"
        ? schema.nullable(true)
        : schema
            .test("required", "ZIP code is required", (value) => {
              return !!value;
            })
            .test("valid", "ZIP code must be valid", (value) => {
              return /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(value);
            });
    }),
  startsAtDate: yup
    .date("Invalid Date")
    .typeError("Invalid Date")
    .required("Start Date is required"),
  startsAtTime: yup
    .date("Invalid Time")
    .typeError("Invalid Time")
    .required("Start Time is required"),
  endsAtDate: yup
    .date("Invalid Date")
    .typeError("Invalid Date")
    .required("End Date is required"),
  endsAtTime: yup
    .date("Invalid Time")
    .typeError("Invalid Time")
    .required("End Time is required"),
});

const buildValuesPayload = (values, event) => {
  const addressFields = [
    "placeName",
    "addressLine1",
    "addressLine2",
    "city",
    "state",
    "zipcode",
  ];
  const { timezone } = event;

  const newValues = { ...values };

  const {
    addressSelection,
    startsAtDate,
    startsAtTime,
    endsAtDate,
    endsAtTime,
    banners,
  } = newValues;
  const timeValues = {
    startsAt: Timestamp.fromDate(
      rezoneMomentDateTime(startsAtDate, startsAtTime, timezone).toDate(),
    ),
    endsAt: Timestamp.fromDate(
      rezoneMomentDateTime(endsAtDate, endsAtTime, timezone).toDate(),
    ),
  };
  delete newValues.startsAtDate;
  delete newValues.startsAtTime;
  delete newValues.endsAtDate;
  delete newValues.endsAtTime;
  const address =
    addressSelection === "useEvent"
      ? null
      : addressFields.reduce((acc, addressField) => {
          const value = newValues[addressField];
          delete newValues[addressField];
          return {
            ...acc,
            [addressField]: value || "",
          };
        }, {});

  // Separate banners that need uploading (have file) from those already uploaded
  const bannersToUpload = (banners || []).filter((banner) => banner.file);
  const uploadedBanners = (banners || []).filter((banner) => !banner.file);

  // Remove file property from banners before saving (keep only key and src)
  // For new sessions, we'll start with empty banners and upload them after creation
  // For existing sessions, keep the already-uploaded banners
  const cleanBanners = uploadedBanners.map(({ key, src }) => ({ key, src }));
  delete newValues.banners;

  const valuesPayload = { ...newValues, address, ...timeValues };

  // Only include banners in payload if there are already-uploaded ones
  // (for existing sessions being updated)
  if (cleanBanners.length > 0) {
    valuesPayload.banners = cleanBanners;
  }

  const sanitizedPerformers = (valuesPayload.performers || [])
    .filter((p) => p && p.performerId)
    .map((performer, index) => ({
      performerId: performer.performerId,
      name: performer.name ?? "",
      email: performer.email ?? "",
      links: Array.isArray(performer.links) ? performer.links : [],
      bio: performer.bio ?? "",
      credits: performer.credits ?? "",
      headshotUrl: performer.headshotUrl ?? "",
      order: performer.order ?? index,
      isHeadliner: performer.isHeadliner ?? false,
    }));
  const performerIds = sanitizedPerformers.map((p) => p.performerId);
  valuesPayload.performers = sanitizedPerformers;
  valuesPayload.performerIds = performerIds;

  const result = Object.keys(valuesPayload).reduce((acc, key) => {
    const _acc = acc;
    if (valuesPayload[key] !== undefined) _acc[key] = valuesPayload[key];
    return _acc;
  }, {});

  // Attach banners to upload separately (for new sessions)
  if (bannersToUpload.length > 0) {
    result._bannersToUpload = bannersToUpload;
  }

  return result;
};

const EditSessionForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [confirmationDialogOpen, setConfirmationDialogOpen] =
    React.useState(false);

  const { event, editingSession, creatingNew, organization } =
    useOutletContext();
  const { addressHistory, id: organizationId } = organization;
  const { id: eventId, timezone, ticketTypes: eventTicketTypes } = event;
  const performersState = useSelector((state) => performersSliceSelector(state));
  const { performers: orgPerformersMap = {}, performersOrganizationId } = performersState;
  const organizationPerformers =
    organizationId && performersOrganizationId === organizationId
      ? Object.values(orgPerformersMap)
      : [];

  useEffect(() => {
    if (organizationId && performersOrganizationId !== organizationId) {
      dispatch(fetchPerformers({ organizationId }));
    }
  }, [dispatch, organizationId, performersOrganizationId]);

  const {
    name,
    description,
    id: sessionId,
    enabled,
    publicFacing,
    startsAt,
    endsAt,
    ticketTypes,
    ticketTypeSelection,
    addressSelection,
    address,
    banners,
    performers,
  } = editingSession || {};

  const { placeName, addressLine1, addressLine2, city, state, zipcode } =
    address || {};

  const startsAtDate = startsAt ? moment.tz(startsAt.toDate(), timezone) : null;
  const startsAtTime = startsAt ? moment.tz(startsAt.toDate(), timezone) : null;
  const endsAtDate = endsAt ? moment.tz(endsAt.toDate(), timezone) : null;
  const endsAtTime = endsAt ? moment.tz(endsAt.toDate(), timezone) : null;

  const descriptionHtml = description || "";
  const [descriptionState, setDescriptionState] = React.useState(
    editorStateFromHtml(descriptionHtml),
  );

  const sanitizedPerformers = (() => {
    const list = performers || [];
    if (list.length === 0) return [];
    const first = list[0];
    if (first.performerId) {
      return list.map((p, index) => ({
        performerId: p.performerId,
        name: p.name ?? "",
        email: p.email ?? "",
        links: Array.isArray(p.links) ? p.links : [],
        bio: p.bio ?? "",
        credits: p.credits ?? "",
        headshotUrl: p.headshotUrl ?? "",
        order: p.order ?? index,
        isHeadliner: p.isHeadliner ?? false,
      }));
    }
    const nameKey = (p) => (p.name || "").trim().toLowerCase();
    const orgList = Object.values(orgPerformersMap);
    return list
      .map((oldP, index) => {
        const nameStr = (oldP.name || "").trim().toLowerCase();
        const match = orgList.find((o) => nameKey(o) === nameStr);
        if (!match) return null;
        return {
          performerId: match.id,
          name: match.name ?? "",
          email: match.email ?? "",
          links: Array.isArray(match.links) ? match.links : [],
          bio: match.bio ?? "",
          credits: match.credits ?? "",
          headshotUrl: match.headshotUrl ?? "",
          order: index,
          isHeadliner: false,
        };
      })
      .filter(Boolean);
  })();

  if (!editingSession) {
    return;
  }

  return (
    <Box sx={{ paddingBottom: "90px" }}>
      <Formik
        initialValues={{
          name,
          description,
          enabled,
          publicFacing: publicFacing || false,
          performers: sanitizedPerformers,
          banners: banners || [],
          startsAtDate,
          startsAtTime,
          endsAtDate,
          endsAtTime,
          ticketTypes,
          ticketTypeSelection,
          addressSelection,
          placeName,
          addressLine1,
          addressLine2,
          city,
          state,
          zipcode,
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, submitProps) => {
          const payload = buildValuesPayload(values, event);
          submitProps.resetForm({ values });
          if (creatingNew) {
            await dispatch(createSession({ eventId, values: payload }));
          } else {
            await dispatch(
              updateSession({ eventId, sessionId, values: payload }),
            );
          }
          navigate("../");
        }}
        enableReinitialize
      >
        {({
          values,
          touched,
          errors,
          handleChange,
          handleBlur,
          isValid,
          dirty,
          setValues,
          setFieldValue,
          setFieldTouched,
          handleReset,
          submitForm,
        }) => {
          return (
            <Form noValidate autoComplete="off">
              <FormPrompt hasUnsavedChanges={dirty} />
              <ConfirmationDialog
                open={confirmationDialogOpen}
                title={"Close?"}
                message={
                  "You have unsaved changes. Save or discard them to continue."
                }
                buttonDefsOverride={[
                  {
                    text: "Cancel",
                    onClick: () => {
                      setConfirmationDialogOpen(false);
                    },
                  },
                  {
                    text: "Discard",
                    onClick: async () => {
                      await handleReset();
                      navigate("../");
                    },
                  },
                  {
                    text: "Save",
                    type: "submit",
                    onClick: () => {
                      setConfirmationDialogOpen(false);
                      submitForm();
                    },
                  },
                ]}
              />
              <Box display={"flex"} flexDirection={"column"}>
                <Box
                  width={"100%"}
                  display={"flex"}
                  alignItems={"center"}
                  gap={"20px"}
                  marginTop={"40px"}
                  marginBottom={"20px"}
                >
                  <Typography variant="h4">
                    {creatingNew ? "New Session" : "Edit Session"}
                  </Typography>
                  <Button
                    onClick={() => {
                      if (dirty) {
                        setConfirmationDialogOpen(true);
                      } else {
                        navigate("../");
                      }
                    }}
                  >
                    Close
                  </Button>
                </Box>
                <TextField
                  required
                  sx={{ width: "100%", marginTop: "20px" }}
                  id="name"
                  name="name"
                  label="Session Name"
                  value={values.name || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name ? errors.name : " "}
                />
                <FormControl sx={{ marginTop: "20px" }}>
                  <FormLabel id={"publicFacing-label"}>
                    Public-Facing Page
                  </FormLabel>
                  <Switch
                    name="publicFacing"
                    checked={values.publicFacing || false}
                    onChange={(e) => {
                      setFieldValue("publicFacing", e.target.checked);
                    }}
                    onBlur={handleBlur}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                  <FormHelperText>
                    Enable to create a public-facing page for this session
                  </FormHelperText>
                </FormControl>
                {values.publicFacing && (
                  <>
                    <SessionMedia
                      eventId={eventId}
                      sessionId={sessionId}
                      banners={values.banners || []}
                      creatingNew={creatingNew}
                      formikProps={{
                        values,
                        setFieldValue,
                      }}
                    />
                    <PerformersForm
                      organizationPerformers={organizationPerformers}
                      formikProps={{
                        values,
                        setFieldValue,
                      }}
                    />
                  </>
                )}
                {!values.publicFacing && (
                  <TextField
                    sx={{ width: "100%", marginTop: "20px" }}
                    id="description"
                    name="description"
                    label="Session Description"
                    value={values.description || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(touched.description && errors.description)}
                    helperText={
                      touched.description && errors.description
                        ? errors.description
                        : " "
                    }
                  />
                )}
                <FormControl sx={{ marginTop: "20px" }}>
                  <FormLabel id={"ticketLimit-label"}>Enabled</FormLabel>
                  <Switch
                    name="enabled"
                    checked={values.enabled}
                    onChange={(e) => {
                      setFieldValue("enabled", e.target.checked);
                    }}
                    onBlur={handleBlur}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                </FormControl>

                <Accordion
                  expanded={values.addressSelection === "specified"}
                  elevation={0}
                  padding={0}
                  disableGutters
                  sx={{
                    "&:before": {
                      display: "none",
                    },
                  }}
                >
                  <AccordionSummary
                    sx={{
                      padding: "0px",
                      flexDirection: "row-reverse",
                      "& .MuiAccordionSummary-content": {
                        marginLeft: "0px",
                        paddingLeft: "0px",
                      },
                      "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                        transform: "rotate(90deg)",
                      },
                    }}
                  >
                    <FormControl sx={{ marginTop: "20px" }}>
                      <FormLabel id={"location-label"}>Location</FormLabel>
                      <RadioGroup
                        aria-labelledby={"location-label"}
                        value={values.addressSelection}
                        name={"addressSelection"}
                        onChange={(e) => {
                          handleChange(e);
                        }}
                      >
                        <Box display={"flex"} gap={"10px"}>
                          <FormControlLabel
                            value="useEvent"
                            control={<Radio />}
                            label="Use event location"
                          />
                          <FormControlLabel
                            value="specified"
                            control={<Radio />}
                            label="Specify location"
                          />
                        </Box>
                      </RadioGroup>
                      <FormHelperText
                        error={errors.address}
                        position={"absolute"}
                        color={"error"}
                        bottom={"-25px"}
                      >
                        &nbsp;
                        {touched.address && errors.address}
                      </FormHelperText>
                    </FormControl>
                  </AccordionSummary>
                  <AccordionDetails sx={{ paddingLeft: "0px" }}>
                    <LocationForm
                      formikProps={{
                        values,
                        handleChange,
                        setFieldValue,
                        setValues,
                        handleBlur,
                        touched,
                        errors,
                      }}
                      marginTop={"0px"}
                      addressHistory={addressHistory}
                      titleRow={
                        <Box>
                          <Typography>Session Location</Typography>
                        </Box>
                      }
                    />
                  </AccordionDetails>
                </Accordion>

                <DateTimeForm
                  formikProps={{
                    values,
                    setFieldValue,
                    touched,
                    errors,
                  }}
                />
                <Accordion
                  expanded={values.ticketTypeSelection === "specified"}
                  elevation={0}
                  padding={0}
                  disableGutters
                  sx={{
                    "&:before": {
                      display: "none",
                    },
                  }}
                >
                  <AccordionSummary
                    sx={{
                      padding: "0px",
                      flexDirection: "row-reverse",
                      "& .MuiAccordionSummary-content": {
                        marginLeft: "0px",
                        paddingLeft: "0px",
                      },
                      "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                        transform: "rotate(90deg)",
                      },
                    }}
                  >
                    <FormControl sx={{ marginTop: "20px" }}>
                      <FormLabel id={"ticketTypes-label"}>
                        Ticket Type Selection
                      </FormLabel>
                      <RadioGroup
                        aria-labelledby={"ticketTypes-label"}
                        value={values.ticketTypeSelection}
                        name={"ticketTypeSelection"}
                        onChange={(e) => {
                          setFieldValue("ticketTypes", []);
                          handleChange(e);
                        }}
                      >
                        <Box display={"flex"} gap={"10px"}>
                          <FormControlLabel
                            value="all"
                            control={<Radio />}
                            label="Grant access to all ticket types"
                          />
                          <FormControlLabel
                            value="specified"
                            control={<Radio />}
                            label="Grant access to selected ticket types"
                          />
                        </Box>
                      </RadioGroup>
                      <FormHelperText
                        error={errors.ticketTypes}
                        position={"absolute"}
                        color={"error"}
                        bottom={"-25px"}
                      >
                        &nbsp;
                        {touched.ticketTypes && errors.ticketTypes}
                      </FormHelperText>
                    </FormControl>
                  </AccordionSummary>
                  <AccordionDetails sx={{ paddingLeft: "0px" }}>
                    <Box display={"flex"} flexDirection={"column"}>
                      <FormLabel>Apply to these ticket types:</FormLabel>
                      {(!eventTicketTypes || eventTicketTypes.length === 0) && (
                        <Typography color={"text.disabled"}>
                          No ticket types defined
                        </Typography>
                      )}
                      {(eventTicketTypes || []).map((ticketType) => {
                        const checked = !!(values.ticketTypes || []).find(
                          (collectForTicketTypeKey) =>
                            collectForTicketTypeKey === ticketType.key,
                        );
                        return (
                          <FormControlLabel
                            key={ticketType.key}
                            sx={{ marginTop: "0px" }}
                            control={
                              <Checkbox
                                checked={checked}
                                onChange={(e) => {
                                  let newArray;
                                  if (e.target.checked) {
                                    newArray = [
                                      ...(values.ticketTypes || []),
                                      ticketType.key,
                                    ];
                                  } else {
                                    newArray = values.ticketTypes.filter(
                                      (collectForTicketTypeKey) =>
                                        collectForTicketTypeKey !==
                                        ticketType.key,
                                    );
                                  }
                                  setFieldValue("ticketTypes", newArray);
                                }}
                              />
                            }
                            label={ticketType.name}
                          />
                        );
                      })}
                    </Box>
                  </AccordionDetails>
                </Accordion>
                {values.publicFacing && (
                  <DescriptionForm
                    formikProps={{
                      values,
                      handleChange,
                    }}
                    descriptionState={descriptionState}
                    handleStateChange={setDescriptionState}
                  />
                )}
              </Box>

              <BottomButtons
                show={creatingNew || dirty}
                handleDiscard={() => {
                  if (creatingNew) {
                    navigate("../");
                  } else {
                    setDescriptionState(editorStateFromHtml(descriptionHtml));
                    handleReset();
                  }
                }}
              />
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default EditSessionForm;
